import React from 'react';
import PropTypes from 'prop-types';

import { bubEvents } from 'shared/events/bub_events';
import { userData } from 'javascripts/shared/utils/user_data';

import { AudiobookCreditNames } from 'javascripts/components/audiobook/audiobook_credit_names';
import { ChirpButton } from 'javascripts/components/chirp_button';
import { ReportAudiobookButton } from 'javascripts/components/audiobook/report_audiobook_button';

import styles from 'stylesheets/components/audiobook/audiobook_details_section';

export function AudiobookDetailsSection({ audiobook }) {

  function trackListClick(listId) {
    bubEvents.track('Book list details', 'Clicked link', {
      label: 'Book list id',
      value: listId,
    });
  }

  function renderList(list, index) {
    return (
      <span key={list.id}>
        <ChirpButton
          attrs={{ target: '_blank', rel: 'noopener noreferrer' }}
          displayStyle='underline'
          displayType='link'
          fontSize='small'
          href={list.url}
          label={list.title}
          onClick={_.partial(trackListClick, list.id)}
        />
        {index < audiobook.featuredLists.length - 1 && ', '}
      </span>
    );
  }

  function renderFeaturedIn() {
    if (_.isEmpty(audiobook.featuredLists)) {
      return null;
    }

    return <div>Featured in: {_.map(audiobook.featuredLists, renderList)}</div>;
  }

  function renderCredits(type) {
    const credits = _.get(audiobook, `${type}Credits`);
    if (credits.length < 3) {
      return null;
    }

    return (
      <>
        {_.capitalize(type)}s: {<AudiobookCreditNames credits={credits} type={type} truncate={false} />}
        <br />
      </>
    );
  }

  function trackTagClick(tagId) {
    bubEvents.track('Book tag details', 'Clicked link', {
      label: 'Book tag id',
      value: tagId,
    });
  }

  function renderTag(tag, index) {
    return (
      <span key={tag.id}>
        <ChirpButton
          attrs={{ target: '_blank', rel: 'noopener noreferrer' }}
          displayStyle='underline'
          displayType='link'
          fontSize='small'
          href={tag.url}
          label={tag.displayName}
          onClick={_.partial(trackTagClick, tag.id)}
        />
        {index < audiobook.tags.length - 1 && ', '}
      </span>
    );
  }

  function renderTags() {
    if (_.isEmpty(audiobook.tags) || userData.isLoggedIn()) {
      return null;
    }

    return <div>Tags: {_.map(audiobook.tags, renderTag)}</div>;
  }

  function renderReportAudiobookButton() {
    return (
      <div className={styles.reportButton}>
        <ReportAudiobookButton audiobook={audiobook} />
      </div>
    );
  }

  return (
    <div data-testid='audiobook-details-section'>
      <h2 className={styles.header}>Details</h2>
      {renderCredits('author')}
      {renderCredits('narrator')}
      Release Date: {audiobook.releaseDate}
      <br />
      Publisher: {audiobook.publisher}
      <br />
      {audiobook.abridged ? 'Abridged' : 'Unabridged'} Audiobook
      {renderFeaturedIn()}
      {renderTags()}
      {renderReportAudiobookButton()}
    </div>
  );
}

AudiobookDetailsSection.propTypes = {
  audiobook: PropTypes.shape({
    abridged: PropTypes.bool.isRequired,
    authorCredits: PropTypes.array,
    featuredLists: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
    narratorCredits: PropTypes.array,
    publisher: PropTypes.string.isRequired,
    releaseDate: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        displayName: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
};
